// src/components/reports/ErrorCellWithTooltip.js
// Table cell that shows a linked count with a lazy-loaded resolved/total tooltip.
// Used in the Unified Event Summary for Integration Errors and EDS Kickouts.

import React from "react";
import { Link } from "react-router-dom";
import { Tooltip } from "@mui/material";
import dayjs from "dayjs";
import * as API from "../api/index";
import { ROUTES } from "../../api/routes";

function getDateRange(period) {
  const today = dayjs.utc().startOf("day");
  switch (period) {
    case "Today":
      return { start: today, end: today.endOf("day") };
    case "Yesterday":
      return {
        start: today.subtract(1, "day"),
        end: today.subtract(1, "day").endOf("day"),
      };
    case "7D":
      return {
        start: today.subtract(7, "day"),
        end: today.subtract(1, "day").endOf("day"),
      };
    case "30D":
      return {
        start: today.subtract(30, "day"),
        end: today.subtract(1, "day").endOf("day"),
      };
    default:
      return { start: today, end: today.endOf("day") };
  }
}

function isBetweenInclusive(d, start, end) {
  return (d.isAfter(start) || d.isSame(start)) && (d.isBefore(end) || d.isSame(end));
}

const TooltipContent = ({ resolved, total, type }) => {
  const pct = ((resolved / total) * 100 || 0).toFixed(1);
  const color = resolved / total >= 0.8 ? "green" : resolved / total >= 0.5 ? "orange" : "red";

  return (
    <div
      style={{
        padding: "6px 10px",
        backgroundColor: "#f0f0f0",
        color: "#333",
        borderRadius: "5px",
        boxShadow: "0px 2px 6px rgba(0,0,0,0.2)",
        fontSize: "0.85rem",
        minWidth: "130px",
      }}
    >
      <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
        {type === "integration" ? "Integration Errors" : "EDS Kickouts"}
      </div>
      <div>
        Resolved: <strong>{resolved}</strong> / <strong>{total}</strong>
      </div>
      <div style={{ textAlign: "right", marginTop: "2px", fontWeight: "bold", color }}>
        {pct}%
      </div>
    </div>
  );
};

const ErrorCellWithTooltip = ({ source, entity, value, period, type }) => {
  const [tooltipContent, setTooltipContent] = React.useState("");

  const resourcePath =
    type === "integration"
      ? ROUTES.errorLogBySourceEntity(source, entity)
      : ROUTES.kickouts.bySourceEntity(source, entity);

  const handleMouseEnter = async () => {
    if (value <= 0 || tooltipContent) return;

    try {
      const data = await API.GetDataFromAPI(resourcePath);
      const { start, end } = getDateRange(period);
      const dateField = type === "integration" ? "ErrorTs" : "InsertDate";

      const filtered = data.filter((r) =>
        isBetweenInclusive(dayjs.utc(r[dateField]), start, end),
      );

      const total = filtered.length;
      const resolved = filtered.filter(
        (r) => r.record_status?.toUpperCase() === "RESOLVED",
      ).length;

      setTooltipContent(<TooltipContent resolved={resolved} total={total} type={type} />);
    } catch (err) {
      console.error(err);
      setTooltipContent("Error fetching stats");
    }
  };

  if (value <= 0) return <span>{value}</span>;

  const to =
    type === "integration"
      ? `/IntegrationErrors/${source}/${entity}/${period}`
      : `/KickoutErrors/${source}/${entity}/${period}`;

  return (
    <Tooltip title={tooltipContent} arrow>
      <Link
        to={to}
        onMouseEnter={handleMouseEnter}
        style={{
          textDecoration: "underline",
          color: "#1976d2",
          cursor: "pointer",
          fontWeight: "500",
        }}
      >
        {value}
      </Link>
    </Tooltip>
  );
};

export default ErrorCellWithTooltip;
